/**
 * threadReceiptsRepository — read receipts for shared threads (2026-09-08, PR-3).
 *
 * Store: `group_thread_messages/{threadId}/receipts/{readerId}` where the
 * reader is a participant uid (client-written, OWN doc only — firestore.rules)
 * or `'assistant'` (server-written by the durable-write path when Prest0n
 * answers; never a uid). One doc per reader, a high-water mark — and, since
 * 2026-09-10, the reader's own PER-MESSAGE marks:
 *   { lastReadOrderTs: int, lastReadMessageId?: string, updatedAt: serverTimestamp,
 *     seen?: { [sharedAnchor]: orderTs } }   // ≤ 400 entries, oldest evicted
 *
 * `queueOwnSeenMarks` is the per-message writer: marks accumulate per
 * (thread, reader) and the WHOLE pending set rides each debounced write —
 * the DebounceManager keeps only the latest task per key, so a task must
 * carry everything still owed, never just the newest mark. Every write is a
 * merge (a nested map deep-merges, so two tabs adding marks never clobber
 * each other); an eviction past the cap sends `deleteField()` sentinels; the
 * high-water mark rides along as the max instant so the ✓✓ accent, Prest0n's
 * receipt and the rules' monotonic guard all keep meaning what they did.
 *
 * Writes are MONOTONIC (rules refuse a decrease; the client never asks for
 * one), debounced 500 ms per thread so a dwell that fires as messages keep
 * landing coalesces into one write, and best-effort: a refusal (a thread whose
 * registry predates the floor backfill, a member whose add is still settling)
 * is swallowed — a receipt is a courtesy to the others, never a gate on
 * anything the viewer does.
 *
 * Reads are one collection listener per open thread with the same fail-closed
 * flag and the same retry ladder as the shared-message overlay
 * (groupThreadMessagesRepository): flag off → no Firestore access, a no-op
 * detach; an error backs off and re-attaches.
 */
import { deleteField, onSnapshot } from 'firebase/firestore';
import {
  assertCurrentUid,
  collection,
  db,
  debouncedSetDoc,
  doc,
  serverTimestamp,
} from './firestoreUtils';
import { resolveGroupThreadReadEnabled } from '../services/groupThreads/featureFlags';
import { GROUP_LISTENER_RETRY_DELAYS_MS } from './groupThreadMessagesRepository';
import {
  ASSISTANT_RECEIPT_ID,
  SEEN_MAP_CAP,
  mergeSeenMarks,
  type ReceiptFacts,
  type ReceiptsByReader,
  type SeenMark,
} from '../services/groupThreads/seenBy';

const GROUP_STORE = 'group_thread_messages';
const RECEIPTS = 'receipts';

export const RECEIPT_WRITE_DEBOUNCE_MS = 500;

/** Local monotonic guard: threadId|uid → the highest mark this session wrote. */
const lastWrittenMark = new Map<string, number>();
/** threadId|uid → marks still owed to the store (the whole set rides every write). */
const pendingSeen = new Map<string, Map<string, number>>();
/** threadId|uid → anchors already in the store (seeded from the viewer's own receipt) or already sent. */
const sentSeen = new Map<string, Set<string>>();

/** Test seam. */
export function resetThreadReceiptsRepositoryForTests(): void {
  lastWrittenMark.clear();
  pendingSeen.clear();
  sentSeen.clear();
}

export function receiptDocRef(threadId: string, readerId: string) {
  return doc(db, GROUP_STORE, threadId, RECEIPTS, readerId);
}

export interface WriteOwnReceiptOptions {
  /** Resolve the account-scoped flag against this account instead of the mount-published one. */
  accountId?: string | null;
}

/**
 * Write the viewer's own receipt. Resolves true when the mark advanced and
 * the write settled on the server; false when skipped (flag off, invalid,
 * not an advance) or refused.
 */
export async function writeOwnReceipt(
  threadId: string,
  uid: string,
  facts: ReceiptFacts,
  opts?: WriteOwnReceiptOptions,
): Promise<boolean> {
  const id = (threadId ?? '').trim();
  const reader = (uid ?? '').trim();
  if (!id || !reader || reader === ASSISTANT_RECEIPT_ID) return false;
  if (!resolveGroupThreadReadEnabled(opts?.accountId ? { accountId: opts.accountId } : {})) return false;
  const mark = facts?.lastReadOrderTs;
  if (typeof mark !== 'number' || !Number.isFinite(mark) || mark < 0) return false;
  const key = `${id}|${reader}`;
  const prev = lastWrittenMark.get(key);
  if (prev !== undefined && mark <= prev) return false;
  try {
    assertCurrentUid(reader, 'writeOwnReceipt');
  } catch {
    return false;
  }
  const data: Record<string, unknown> = {
    lastReadOrderTs: Math.floor(mark),
    updatedAt: serverTimestamp(),
  };
  const msgId = typeof facts.lastReadMessageId === 'string' ? facts.lastReadMessageId.trim() : '';
  if (msgId) data.lastReadMessageId = msgId;
  const ref = receiptDocRef(id, reader);
  try {
    await debouncedSetDoc(ref, data, { debounceKey: ref.path, debounceMs: RECEIPT_WRITE_DEBOUNCE_MS });
    const nowPrev = lastWrittenMark.get(key);
    if (nowPrev === undefined || mark > nowPrev) lastWrittenMark.set(key, Math.floor(mark));
    return true;
  } catch {
    return false; // best-effort: a refused receipt never surfaces
  }
}

/** The per-message map as stored, sanitised: string → finite non-negative instant; junk dropped; absent when the field is not a map. */
export function parseSeenMap(raw: unknown): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    const id = (k ?? '').trim();
    if (!id || typeof v !== 'number' || !Number.isFinite(v) || v < 0) continue;
    out[id] = Math.floor(v);
  }
  return out;
}

/** Parse one receipts snapshot into the reader map; malformed docs are dropped. */
export function parseReceiptsSnapshot(
  docs: ReadonlyArray<{ id: string; data: () => unknown }>,
): ReceiptsByReader {
  const out: ReceiptsByReader = {};
  for (const d of docs) {
    const reader = (d?.id ?? '').trim();
    if (!reader) continue;
    const raw = (typeof d.data === 'function' ? d.data() : null) as Record<string, unknown> | null;
    const mark = raw?.lastReadOrderTs;
    if (typeof mark !== 'number' || !Number.isFinite(mark) || mark < 0) continue;
    const facts: ReceiptFacts = { lastReadOrderTs: Math.floor(mark) };
    const msgId = raw?.lastReadMessageId;
    if (typeof msgId === 'string' && msgId.trim()) facts.lastReadMessageId = msgId.trim();
    const seen = parseSeenMap(raw?.seen);
    if (seen) facts.seen = seen;
    out[reader] = facts;
  }
  return out;
}

export interface QueueOwnSeenMarksOptions extends WriteOwnReceiptOptions {
  /** The viewer's own stored map (from the receipts snapshot) — anchors already there are never re-sent, and the cap is judged against it. */
  knownSeen?: Readonly<Record<string, number>> | null;
}

/**
 * Queue the viewer's own per-message marks (2026-09-10). Marks accumulate per
 * (thread, reader); each debounced write carries the WHOLE pending set plus
 * the high-water mark they imply. Resolves true when a write was scheduled
 * (it settles behind the debounce), false when nothing was owed or the
 * write was skipped (flag off, identity mismatch, junk). A refused write
 * keeps the marks pending for the next batch.
 */
export async function queueOwnSeenMarks(
  threadId: string,
  uid: string,
  marks: ReadonlyArray<SeenMark>,
  opts?: QueueOwnSeenMarksOptions,
): Promise<boolean> {
  const id = (threadId ?? '').trim();
  const reader = (uid ?? '').trim();
  if (!id || !reader || reader === ASSISTANT_RECEIPT_ID) return false;
  if (!resolveGroupThreadReadEnabled(opts?.accountId ? { accountId: opts.accountId } : {})) return false;
  try {
    assertCurrentUid(reader, 'queueOwnSeenMarks');
  } catch {
    return false;
  }
  const key = `${id}|${reader}`;
  const known = opts?.knownSeen && typeof opts.knownSeen === 'object' ? opts.knownSeen : {};
  let sent = sentSeen.get(key);
  if (!sent) {
    sent = new Set<string>();
    sentSeen.set(key, sent);
  }
  for (const k of Object.keys(known)) sent.add(k);
  let pending = pendingSeen.get(key);
  if (!pending) {
    pending = new Map<string, number>();
    pendingSeen.set(key, pending);
  }
  for (const mark of marks ?? []) {
    const anchor = (mark?.id ?? '').trim();
    if (!anchor || anchor.length > 200) continue;
    if (typeof mark.orderTs !== 'number' || !Number.isFinite(mark.orderTs) || mark.orderTs < 0) continue;
    if (sent.has(anchor) || pending.has(anchor)) continue;
    pending.set(anchor, Math.floor(mark.orderTs));
  }
  if (pending.size === 0) return false;

  const adds: SeenMark[] = Array.from(pending.entries()).map(([anchor, orderTs]) => ({ id: anchor, orderTs }));
  const merged = mergeSeenMarks(known, adds, SEEN_MAP_CAP);
  const seen: Record<string, unknown> = {};
  for (const [anchor, orderTs] of pending) {
    if (Object.prototype.hasOwnProperty.call(merged.seen, anchor)) seen[anchor] = orderTs;
  }
  for (const anchor of merged.evictedIds) seen[anchor] = deleteField();
  const prevMark = lastWrittenMark.get(key) ?? 0;
  const impliedMark = Math.max(prevMark, merged.maxOrderTs ?? 0);
  const data: Record<string, unknown> = {
    seen,
    lastReadOrderTs: impliedMark,
    updatedAt: serverTimestamp(),
  };
  if (merged.maxId && merged.maxOrderTs !== null && merged.maxOrderTs >= prevMark) data.lastReadMessageId = merged.maxId;
  const ref = receiptDocRef(id, reader);
  const batch = new Map(pending);
  try {
    await debouncedSetDoc(ref, data, { debounceKey: ref.path, debounceMs: RECEIPT_WRITE_DEBOUNCE_MS });
    for (const anchor of batch.keys()) {
      sent.add(anchor);
      pending.delete(anchor);
    }
    const nowPrev = lastWrittenMark.get(key);
    if (nowPrev === undefined || impliedMark > nowPrev) lastWrittenMark.set(key, impliedMark);
    return true;
  } catch {
    return true; // scheduled; refused — the marks stay pending for the next batch
  }
}

/**
 * Listen to a thread's receipts. Every snapshot — including an empty one — is
 * handed on, so a reader map goes back to {} when the store says nobody has
 * read anything (unlike the message overlay, absence here is information).
 */
export function subscribeThreadReceipts(
  threadId: string,
  onReceipts: (receipts: ReceiptsByReader) => void,
  opts?: { accountId?: string | null },
): () => void {
  if (!resolveGroupThreadReadEnabled(opts?.accountId ? { accountId: opts.accountId } : {})) return () => {};
  const id = (threadId ?? '').trim();
  if (!id) return () => {};

  let stopped = false;
  let detachInner: (() => void) | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const scheduleRetry = (): void => {
    if (stopped) return;
    const delay = GROUP_LISTENER_RETRY_DELAYS_MS[
      Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)
    ];
    retryIndex += 1;
    retryTimer = setTimeout(attach, delay);
  };

  const attach = (): void => {
    if (stopped) return;
    try {
      const unsubscribe = onSnapshot(
        collection(db, GROUP_STORE, id, RECEIPTS),
        (snap) => {
          retryIndex = 0;
          onReceipts(parseReceiptsSnapshot(snap.docs));
        },
        () => {
          try { unsubscribe(); } catch { /* already detached */ }
          scheduleRetry();
        },
      );
      detachInner = unsubscribe;
    } catch {
      scheduleRetry();
    }
  };

  attach();

  return () => {
    stopped = true;
    if (retryTimer !== null) clearTimeout(retryTimer);
    try { detachInner?.(); } catch { /* already detached */ }
  };
}
